import React, { useState, useRef } from 'react';
import { X, Loader2, FileText, Save, AlertCircle, Truck } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';

/**
 * AttachTransportInvoiceModal
 * Used to attach (or re-attach) a transport invoice to an EXISTING shipment.
 *
 * Props:
 *   open: boolean
 *   shipment: { id, supplier, purchaseInvoiceNumber, totalKg, purchaseTotal, transportInvoiceNumber?, transportCost?, ... }
 *   onClose: () => void
 *   onSaved: (updatedShipment) => void
 */
const AttachTransportInvoiceModal = ({ open, shipment, onClose, onSaved }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [transport, setTransport] = useState({
    carrier: shipment?.transportCarrier || '',
    invoiceNumber: shipment?.transportInvoiceNumber || '',
    invoiceDate: shipment?.transportInvoiceDate || '',
    total: shipment?.transportCost != null ? String(shipment.transportCost) : '',
    documentUrl: shipment?.transportInvoiceUrl || '',
    notes: shipment?.notes || '',
  });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Reset when shipment changes
  React.useEffect(() => {
    if (open) {
      setTransport({
        carrier: shipment?.transportCarrier || '',
        invoiceNumber: shipment?.transportInvoiceNumber || '',
        invoiceDate: shipment?.transportInvoiceDate || '',
        total: shipment?.transportCost != null ? String(shipment.transportCost) : '',
        documentUrl: shipment?.transportInvoiceUrl || '',
        notes: shipment?.notes || '',
      });
      setFile(null);
      setError('');
    }
  }, [open, shipment]);

  if (!open || !shipment) return null;

  const totalKg = parseFloat(shipment.totalKg) || 0;
  const transportTotal = parseFloat(transport.total) || 0;
  const transportPerKg = totalKg > 0 ? transportTotal / totalKg : 0;
  const materialPerKg = totalKg > 0 && shipment.purchaseTotal ? shipment.purchaseTotal / totalKg : 0;
  const landedPerKg = materialPerKg + transportPerKg;

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/parse/transport-invoice`, { method: 'POST', body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || 'Parse failed');
      }
      const data = await res.json();
      const p = data.parsed || {};
      setTransport(prev => ({
        carrier: p.carrier || prev.carrier,
        invoiceNumber: p.invoiceNumber || prev.invoiceNumber,
        invoiceDate: p.invoiceDate || prev.invoiceDate,
        total: p.total != null ? String(p.total) : prev.total,
        documentUrl: data.documentUrl || prev.documentUrl,
        notes: p.notes || prev.notes,
      }));
    } catch (e) {
      setError(e.message || 'Parse failed');
    } finally {
      setParsing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const body = {
        transportCarrier: transport.carrier || null,
        transportInvoiceNumber: transport.invoiceNumber || null,
        transportInvoiceDate: transport.invoiceDate || null,
        transportInvoiceUrl: transport.documentUrl || null,
        transportCost: transport.total ? parseFloat(transport.total) : null,
        notes: transport.notes || null,
      };
      const res = await fetch(`${API_URL}/api/shipments/${shipment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || 'Save failed');
      }
      const updated = await res.json();
      onSaved?.(updated);
      onClose?.();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="attach-transport-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2 text-white">
            <Truck className="w-5 h-5 text-purple-400" />
            <h2 className="font-semibold">Attach Transport Invoice</h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Context */}
          <div className="text-xs text-zinc-400 bg-zinc-800/30 rounded p-2 border border-zinc-800">
            For shipment <span className="text-zinc-200 font-medium">{shipment.supplier || '—'}</span>
            {' · '}PO #{shipment.purchaseInvoiceNumber || '—'}
            {' · '}{totalKg.toFixed(0)} kg
            {shipment.purchaseTotal != null && <> · €{shipment.purchaseTotal.toFixed(2)} material</>}
          </div>

          {/* Upload + parse */}
          <div className="flex flex-col gap-2 items-stretch">
            <Input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/*,.pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="bg-zinc-800 border-zinc-700 text-zinc-200 file:text-zinc-200 file:bg-zinc-700 file:border-0 file:px-3 file:py-1 file:rounded file:mr-3"
              data-testid="attach-transport-file-input"
            />
            {file && <div className="text-xs text-zinc-500 flex items-center gap-1"><FileText className="w-3 h-3" /> {file.name} ({Math.round(file.size / 1024)} KB)</div>}
            <Button onClick={handleParse} disabled={!file || parsing} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="attach-transport-parse-btn">
              {parsing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Parsing…</> : 'Parse Transport Invoice'}
            </Button>
          </div>

          {/* Fields */}
          <div className="bg-zinc-800/40 border border-zinc-800 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Carrier</Label>
              <Input value={transport.carrier} onChange={e => setTransport({ ...transport, carrier: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" data-testid="attach-carrier" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Invoice #</Label>
              <Input value={transport.invoiceNumber} onChange={e => setTransport({ ...transport, invoiceNumber: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" data-testid="attach-invoice-number" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Date</Label>
              <Input type="date" value={transport.invoiceDate} onChange={e => setTransport({ ...transport, invoiceDate: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Total Cost (€)</Label>
              <Input type="number" step="any" value={transport.total} onChange={e => setTransport({ ...transport, total: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" data-testid="attach-total" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">Document URL</Label>
              <Input value={transport.documentUrl} onChange={e => setTransport({ ...transport, documentUrl: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" />
            </div>
            <div className="sm:col-span-3">
              <Label className="text-xs text-zinc-400">Notes</Label>
              <Input value={transport.notes} onChange={e => setTransport({ ...transport, notes: e.target.value })} className="bg-zinc-800 border-zinc-700 text-zinc-200" />
            </div>
          </div>

          {transport.total && totalKg > 0 && (
            <div className="bg-emerald-900/20 border border-emerald-900/40 rounded-lg p-3 text-xs text-zinc-300 space-y-0.5" data-testid="attach-landed-preview">
              {materialPerKg > 0 && <div>Material: €{materialPerKg.toFixed(4)}/kg</div>}
              <div>Transport: €{transportPerKg.toFixed(4)}/kg</div>
              <div className="text-emerald-300 font-semibold pt-0.5">Landed: €{landedPerKg.toFixed(4)}/kg</div>
              <div className="text-zinc-500 pt-1">Existing FIFO batches from this shipment will be recomputed automatically.</div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 text-sm text-red-400 bg-red-950/30 border border-red-900/40 rounded p-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-3 border-t border-zinc-800">
          <Button variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !transport.total} className="bg-green-600 hover:bg-green-700 text-white" data-testid="attach-transport-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />Save Transport</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AttachTransportInvoiceModal;
